import React from 'react'

const MicrophoneLayout = ({
  connectedToLiveKit = 'idle',
  isSpeaking = null
}) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <style>{`
        .mic-border-rotator {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .mic-border {
          position: relative;
          width: 70px;
          height: 70px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(102, 126, 234, 0.3);
          transition: all 0.3s ease;
          z-index: 2;
        }

        .mic-border svg {
          transition: all 0.3s ease;
        }

        /* Idle state */
        .mic-border-rotator.idle {
          background: transparent;
        }

        /* Connecting state - rotating border */
        .mic-border-rotator.connecting::before {
          content: '';
          position: absolute;
          inset: -3px;
          padding: 3px;
          background: conic-gradient(from 0deg, #667eea, #764ba2, #667eea);
          border-radius: 50%;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          animation: rotate 2s linear infinite;
        }

        .mic-border-rotator.connecting .mic-border {
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(102, 126, 234, 0.8);
          animation: pulse-connecting 1.5s ease-in-out infinite;
        }

        /* Connected state - pulsing rings */
        .mic-border-rotator.connected::before {
          content: '';
          position: absolute;
          inset: -9px;
          border: 2px solid rgba(102, 126, 234, 0.3);
          border-radius: 50%;
          animation: pulse-ring-1 2s ease-in-out infinite;
        }

        .mic-border-rotator.connected::after {
          content: '';
          position: absolute;
          inset: -14px;
          border: 2px solid rgba(102, 126, 234, 0.2);
          border-radius: 50%;
          animation: pulse-ring-2 2s ease-in-out infinite 0.5s;
        }

        /* Speaking state - Siri-like waves with multicolor */
        .mic-border-rotator.speaking.ai-speaking::before {
          content: '';
          position: absolute;
          inset: -7px;
          border: 3px solid rgba(255, 107, 107, 0.6);
          border-radius: 50%;
          animation: siri-wave-1 0.8s ease-in-out infinite alternate;
        }

        .mic-border-rotator.speaking.ai-speaking::after {
          content: '';
          position: absolute;
          inset: -13px;
          border: 2px solid rgba(78, 205, 196, 0.4);
          border-radius: 50%;
          animation: siri-wave-2 1.2s ease-in-out infinite alternate;
        }

        .mic-border-rotator.speaking.ai-speaking .mic-border {
          animation: siri-center 0.6s ease-in-out infinite alternate;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: none;
          box-shadow: 0 0 30px rgba(255, 107, 107, 0.3), 0 0 60px rgba(78, 205, 196, 0.2);
        }

        /* User speaking - keep original blue */
        .mic-border-rotator.speaking.user-speaking::before {
          content: '';
          position: absolute;
          inset: -7px;
          border: 3px solid rgba(102, 126, 234, 0.6);
          border-radius: 50%;
          animation: siri-wave-1 0.8s ease-in-out infinite alternate;
        }

        .mic-border-rotator.speaking.user-speaking::after {
          content: '';
          position: absolute;
          inset: -13px;
          border: 2px solid rgba(102, 126, 234, 0.4);
          border-radius: 50%;
          animation: siri-wave-2 1.2s ease-in-out infinite alternate;
        }

        .mic-border-rotator.speaking.user-speaking .mic-border {
          animation: siri-center 0.6s ease-in-out infinite alternate;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(102, 126, 234, 0.8);
          box-shadow: 0 0 30px rgba(102, 126, 234, 0.5);
        }

        /* Additional rings for AI speaking */
        .speaking-rings.ai-speaking {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .speaking-rings.ai-speaking::before {
          content: '';
          position: absolute;
          inset: -19px;
          border: 1px solid rgba(69, 183, 209, 0.3);
          border-radius: 50%;
          animation: siri-wave-3 1.6s ease-in-out infinite alternate;
        }

        .speaking-rings.ai-speaking::after {
          content: '';
          position: absolute;
          inset: -25px;
          border: 1px solid rgba(150, 206, 180, 0.2);
          border-radius: 50%;
          animation: siri-wave-4 2s ease-in-out infinite alternate;
        }

        /* Additional rings for user speaking */
        .speaking-rings.user-speaking {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .speaking-rings.user-speaking::before {
          content: '';
          position: absolute;
          inset: -19px;
          border: 1px solid rgba(102, 126, 234, 0.3);
          border-radius: 50%;
          animation: siri-wave-3 1.6s ease-in-out infinite alternate;
        }

        .speaking-rings.user-speaking::after {
          content: '';
          position: absolute;
          inset: -25px;
          border: 1px solid rgba(102, 126, 234, 0.2);
          border-radius: 50%;
          animation: siri-wave-4 2s ease-in-out infinite alternate;
        }

        /* Keyframe animations */
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes rainbow-shift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        @keyframes pulse-connecting {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        @keyframes pulse-ring-1 {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.4;
          }
          100% {
            transform: scale(1.4);
            opacity: 0;
          }
        }

        @keyframes pulse-ring-2 {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.3);
            opacity: 0.3;
          }
          100% {
            transform: scale(1.6);
            opacity: 0;
          }
        }

        @keyframes siri-wave-1 {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.3);
            opacity: 0.4;
          }
        }

        @keyframes siri-wave-2 {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          100% {
            transform: scale(1.5);
            opacity: 0.2;
          }
        }

        @keyframes siri-wave-3 {
          0% {
            transform: scale(1);
            opacity: 0.4;
          }
          100% {
            transform: scale(1.7);
            opacity: 0.1;
          }
        }

        @keyframes siri-wave-4 {
          0% {
            transform: scale(1);
            opacity: 0.3;
          }
          100% {
            transform: scale(1.9);
            opacity: 0.05;
          }
        }

        @keyframes siri-center {
          0% {
            transform: scale(1);
          }
          100% {
            transform: scale(1.1);
          }
        }
      `}</style>

      <div
        className={`mic-border-rotator ${connectedToLiveKit} ${
          (isSpeaking === 'user' || isSpeaking === 'ai') &&
          connectedToLiveKit === 'connected'
            ? `speaking ${
                isSpeaking === 'ai' ? 'ai-speaking' : 'user-speaking'
              }`
            : ''
        }`}
      >
        <div className="mic-border">
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke={
              isSpeaking === 'ai' && connectedToLiveKit === 'connected'
                ? '#ff6b6b'
                : '#0068ff'
            }
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </div>
        {(isSpeaking === 'user' || isSpeaking === 'ai') &&
          connectedToLiveKit === 'connected' && (
            <div
              className={`speaking-rings ${
                isSpeaking === 'ai' ? 'ai-speaking' : 'user-speaking'
              }`}
            />
          )}
      </div>
    </div>
  )
}

export default MicrophoneLayout
